import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, Type } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LogIndexedDbService, LogInstanceToken, PaceLoggingModule } from '@evorait/pace-logging';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { RatingModule } from 'ngx-bootstrap/rating';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { ClipboardModule } from 'ngx-clipboard';
import { tap } from 'rxjs/operators';
import { AccordionComponent } from './components/accordion/accordion.component';
import { AppHeaderComponent } from './components/app-header/app-header.component';
import { BarcodeScannerModalComponent } from './components/barcode-scanner-modal/barcode-scanner-modal.component';
import { CheckboxInputComponent } from './components/checkbox-input/checkbox-input.component';
import { CorporateInformationComponent } from './components/corporate-information/corporate-information.component';
import { DateInputComponent } from './components/date-input/date-input.component';
import { DatetimeInputComponent } from './components/datetime-input/datetime-input.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { SearchFilterPipe } from './components/dropdown/search-filter.pipe';
import { DurationManualInputComponent } from './components/duration-stepper/duration-manual-input/duration-manual-input.component';
import { DurationStepperComponent } from './components/duration-stepper/duration-stepper.component';
import { ErrorLogComponent } from './components/error-log/error-log.component';
import { EscalationComponent } from './components/escalation/escalation.component';
import { FilePathComponent } from './components/file-path/file-path.component';
import { InitializationProgressComponent } from './components/initialization-progress/initialization-progress.component';
import { LoadingScreenComponent } from './components/loading-screen/loading-screen.component';
import { MaterialPickupComponent } from './components/material-pickup/material-pickup.component';
import { MockComponent } from './components/mock-component/mock.component';
import { NavigationLinkComponent } from './components/navigation-link/navigation-link.component';
import { NumberInputComponent } from './components/number-input/number-input.component';
import { NumberStepperComponent } from './components/number-stepper/number-stepper.component';
import { RadioInputComponent } from './components/radio-input/radio-input.component';
import { RatingInputComponent } from './components/rating-input/rating-input.component';
import { ReportIssueComponent } from './components/report-issue/report-issue.component';
import { MenuEntryComponent } from './components/side-menu/menu-entry.component';
import { SideMenuComponent } from './components/side-menu/side-menu.component';
import { SideMenuService } from './components/side-menu/side-menu.service';
import { SortableListComponent } from './components/sortable-list/sortable-list.component';
import { SyncIndicatorComponent } from './components/sync-indicator/sync-indicator.component';
import { TextInputComponent } from './components/text-input/text-input.component';
import { TimeInputComponent } from './components/time-input/time-input.component';
import { TimeManualInputComponent } from './components/time-input/time-manual-input/time-manual-input.component';
import { TreeViewComponent } from './components/tree-view/tree-view.component';
import { TreeComponent } from './components/tree/tree.component';
import { UserMasterComponent } from './components/user-master/user-master.component';
import { WhatsNewComponent } from './components/whats-new/whats-new.component';
import { YesNoModalDialogComponent } from './components/yes-no-modal-dialog/yes-no-modal-dialog.component';
import { LinkDirective } from './directives/link/link.directive';
import { NumberOnlyDirective } from './directives/number-only/number-only.directive';
import { TextInputRestrictionDirective } from './directives/text-input-restriction/text-input-restriction.directive';
import { AppVersion, AppVersionInstanceToken } from './interfaces/app-version.interface';
import { ClientManagerInstanceToken } from './interfaces/client-manager.interface';
import { ConfigRequestsInstanceToken, ConfigRequestsInterface } from './interfaces/config-requests.interface';
import { EnvironmentInstanceToken, PaceEnvironment } from '@evorait/pace-environment';
import { FileStorageInjectionToken } from './interfaces/file-storage.interface';
import { InitializationInstanceToken, InitializationInterface } from './interfaces/initialization.interface';
import { OdataConfigInstanceToken, OdataConfigInterface } from './interfaces/odata-config.interface';
import { OdataValueHelperInstanceToken, OdataValueHelperInterface } from './interfaces/odata-value-helper.interface';
import { SideMenuInstanceToken, SideMenuInterface } from './interfaces/side-menu.interface';
import { SynchronizationInstanceToken, SynchronizationInterface } from './interfaces/synchronization.interface';
import { FilterPipe } from './pipes/filter/filter.pipe';
import { FormatDatePipe } from './pipes/format-date/format-date.pipe';
import { FormatDurationPipe } from './pipes/format-duration/format-duration.pipe';
import { LocalCurrencyPipe } from './pipes/local-currency/local-currency.pipe';
import { LocalDatePipe } from './pipes/local-date/local-date.pipe';
import { LocalNumberPipe } from './pipes/local-number/local-number.pipe';
import { OrderByPipe } from './pipes/order-by/order-by.pipe';
import { AppNotificationComponent } from './services/app-notification/app-notification.component';
import { AppNotificationService } from './services/app-notification/app-notification.service';
import { BarcodeScannerService } from './services/barcode-scanner/barcode-scanner.service';
import { ClientManagerService } from './services/client-manager/client-manager.service';
import { ConfigService } from './services/config/config.service';
import { EnvironmentPathUtilitiesService } from './services/environment-path-utilities/environment-path-utilities.service';
import { EventService } from './services/event/event.service';
import { FileDexieService } from './services/file-storage/file-dexie.service';
import { IndexedDbStorageService } from './services/file-storage/indexed-db-storage.service';
import { HttpUtilitiesService } from './services/http-utilities/http-utilities.service';
import { MeasurementToolsService } from './services/measurement-tools/measurement-tools.service';
import { OnlineOnlyService } from './services/online-only/online-only.service';
import { ReportIssueService } from './services/report-issue/report-issue.service';
import { SoftwareLicencesStorageService } from './services/software-licences/software-licences-storage.service';
import { SoftwareLicencesService } from './services/software-licences/software-licences.service';
import { SyncQueueService } from './services/synchronization/sync-queue.service';
import { TranslationService } from './services/translation/translation.service';
import { UriUtilitiesService } from './services/uri-utilities/uri-utilities.service';
import { UserGidService } from './services/user-gid/user-gid.service';
import { UserMasterService } from './services/user-master/user-master.service';
import { ValueHelperService } from './services/value-helper/value-helper.service';
import { WeeklyWorkTimeService } from './services/weekly-work-time/weekly-work-time.service';
import { WindowService } from './services/window/window.service';
import { DateManipulationCheckService } from './services/date-manipulation-check/date-manipulation-check.service';
import { AppLockComponent } from './services/date-manipulation-check/app-lock.component';
import { FormValidationMessageDirective } from './directives/form-validation-message/form-validation-message.directive';
import { LetDirective } from '@ngrx/component';
import { BackendErrorMessageModalComponent } from './components/backend-error-message-modal/backend-error-message-modal.component';
import { DashboardButtonComponent } from './services/dashboard-button/dashboard-button.component';
import { DashboardButtonService } from './services/dashboard-button/dashboard-button.service';
import {
    PaceBarcodeClipboardComponent,
    PaceBarcodeScannerComponent,
    PaceChangelogComponent,
} from '@evorait/pace-components';
import { BarcodeClipboardModalComponent } from './components/barcode-clipboard-modal/barcode-clipboard-modal.component';
import { TabsRoleDirective } from './directives/tabs-role/tabs-role.directive';
import { FormatSapValuePipe } from './pipes/format-sap-value/format-sap-value.pipe';
import { MemoizePipe } from './pipes/memoize/memoize.pipe';
import { OtherTasksDexieDbService } from './services/other-tasks/other-tasks-dexie-db.service';
import { ChangelogComponent } from './components/changelog/changelog.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { AppointmentBookingService } from './services/appointment-booking/appointment-booking.service';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PaceUrlPipe } from './pipes/url/pace-url.pipe';
import { UAParser } from 'ua-parser-js';
import { PacePartnerZgidErrorModalComponent } from './components/pace-partner-zgid-error-modal/pace-partner-zgid-error-modal.component';
import { RoundMilesPipe } from './pipes/round-miles/round-miles.pipe';

const components = [
    AccordionComponent,
    YesNoModalDialogComponent,
    LoadingScreenComponent,
    AppNotificationComponent,
    AppHeaderComponent,
    AppLockComponent,
    NavigationLinkComponent,
    CorporateInformationComponent,
    InitializationProgressComponent,
    MenuEntryComponent,
    SideMenuComponent,
    WhatsNewComponent,
    SyncIndicatorComponent,
    ReportIssueComponent,
    UserMasterComponent,
    EscalationComponent,
    FilePathComponent,
    MaterialPickupComponent,
    CheckboxInputComponent,
    DateInputComponent,
    DatetimeInputComponent,
    DropdownComponent,
    DurationStepperComponent,
    DurationManualInputComponent,
    BarcodeScannerModalComponent,
    BarcodeClipboardModalComponent,
    NumberInputComponent,
    NumberStepperComponent,
    RadioInputComponent,
    RatingInputComponent,
    SortableListComponent,
    TextInputComponent,
    TimeInputComponent,
    TimeManualInputComponent,
    TreeComponent,
    TreeViewComponent,
    MockComponent,
    ErrorLogComponent,
    BackendErrorMessageModalComponent,
    DashboardButtonComponent,
    ChangelogComponent,
    FileUploadComponent,
    PacePartnerZgidErrorModalComponent,
];

const pipes = [
    SearchFilterPipe,
    FilterPipe,
    OrderByPipe,
    MemoizePipe,
    LocalCurrencyPipe,
    LocalDatePipe,
    LocalNumberPipe,
    FormatDurationPipe,
    FormatDatePipe,
    FormatSapValuePipe,
    PaceUrlPipe,
    RoundMilesPipe,
];

const directives = [
    LinkDirective,
    NumberOnlyDirective,
    TextInputRestrictionDirective,
    FormValidationMessageDirective,
    TabsRoleDirective,
];

@NgModule({
    declarations: [...components, ...pipes, ...directives],
    exports: [...components, ...pipes, ...directives, ModalModule],
    imports: [
        CommonModule,
        CollapseModule,
        ModalModule,
        FontAwesomeModule,
        PopoverModule,
        ProgressbarModule,
        TranslateModule.forChild(),
        FormsModule,
        ReactiveFormsModule,
        RatingModule,
        ZXingScannerModule,
        ClipboardModule,
        PaceLoggingModule.forRoot(),
        LetDirective,
        PaceBarcodeScannerComponent,
        PaceBarcodeClipboardComponent,
        PaceChangelogComponent,
        BsDatepickerModule,
        TooltipModule,
    ],
})
export class PaceCoreModule {
    public constructor(
        private readonly translationService: TranslationService,
        private readonly translate: TranslateService,
    ) {
        // load default language translation (for fallback)
        const defaultTranslation = <Record<string, unknown>>(
            // eslint-disable-next-line @typescript-eslint/no-var-requires,@typescript-eslint/no-require-imports
            require(`libs/pace-core/src/assets/i18n/${this.translationService.defaultLanguageKey}.json`)
        );
        this.translate.setTranslation(this.translationService.defaultLanguageKey, defaultTranslation, true);

        this.translationService.currentLanguage$
            .pipe(
                tap((lang) => {
                    if (lang !== this.translationService.defaultLanguageKey) {
                        // load device's language translation
                        const translation = <Record<string, unknown>>(
                            // eslint-disable-next-line @typescript-eslint/no-var-requires,@typescript-eslint/no-require-imports
                            require(`libs/pace-core/src/assets/i18n/${lang}.json`)
                        );
                        this.translate.setTranslation(lang, translation, true);
                    }

                    this.translate.use(lang);
                }),
            )
            .subscribe();
    }

    public static forRoot(configuration: PaceCoreModuleConfiguration): ModuleWithProviders<PaceCoreModule> {
        return {
            ngModule: PaceCoreModule,
            providers: [
                CommonModule,
                FormatDurationPipe,
                FileDexieService,
                OnlineOnlyService,
                SyncQueueService,
                ConfigService,
                UserMasterService,
                ValueHelperService,
                AppNotificationService,
                EventService,
                EnvironmentPathUtilitiesService,
                UriUtilitiesService,
                HttpUtilitiesService,
                ClientManagerService,
                WindowService,
                DateManipulationCheckService,
                TranslationService,
                SideMenuService,
                WeeklyWorkTimeService,
                ReportIssueService,
                SoftwareLicencesService,
                SoftwareLicencesStorageService,
                UserGidService,
                MeasurementToolsService,
                DashboardButtonService,
                BarcodeScannerService,
                OtherTasksDexieDbService,
                AppointmentBookingService,
                // load the corresponding logging mechanism, depending on the OS
                {
                    // TODO remove this later as right now it is being also provided from pace-logging module
                    // which will create two different instances if we use lazy loading
                    // Hint: LogCesLibService should be part of pace-logging after our auth changes are done.
                    provide: LogInstanceToken,
                    useClass: LogIndexedDbService,
                },
                // load indexedDBFileStorage for browser specific apps
                {
                    provide: FileStorageInjectionToken,
                    useClass: IndexedDbStorageService,
                },
                { provide: ClientManagerInstanceToken, useExisting: ClientManagerService },
                { provide: EnvironmentInstanceToken, useValue: configuration.environment },
                { provide: AppVersionInstanceToken, useValue: configuration.appVersion },
                { provide: InitializationInstanceToken, useExisting: configuration.initializationService },
                { provide: ConfigRequestsInstanceToken, useExisting: configuration.configInterfaceService },
                { provide: OdataConfigInstanceToken, useExisting: configuration.odataConfigService },
                { provide: OdataValueHelperInstanceToken, useExisting: configuration.odataValueHelper },
                { provide: SideMenuInstanceToken, useExisting: configuration.sideMenuService },
                { provide: SynchronizationInstanceToken, useExisting: configuration.synchronizationService },
                { provide: UAParser, useFactory: () => new UAParser() },
            ],
        };
    }

    public static forChild(): ModuleWithProviders<PaceCoreModule> {
        return {
            ngModule: PaceCoreModule,
        };
    }
}

export interface PaceCoreModuleConfiguration {
    environment: PaceEnvironment;
    appVersion: AppVersion;
    initializationService: Type<InitializationInterface>;
    odataConfigService: Type<OdataConfigInterface>;
    odataValueHelper: Type<OdataValueHelperInterface>;
    sideMenuService: Type<SideMenuInterface>;
    synchronizationService: Type<SynchronizationInterface>;
    configInterfaceService: Type<ConfigRequestsInterface>;
}
